import React, { Component } from 'react';
import { isEqual, cloneDeep } from 'lodash';

import { session } from '../../../services/session';
import { getClass } from '../../../utils/component-helpers';
import { dateService } from '../../../services/date';
import { formatValue } from '../../base/common/utils';

import StrategySelection from './strategy-selection';
import StrategyDatePicker from './strategy-date-picker';
import StrategyTypeahead from './strategy-typeahead';
import StrategyVisibility from './strategy-visibility';
import StrategyFeeType from './strategy-fee-type';
import ProductTypeahead from './product-typeahead';
import BasicDropdown from '../../base/dropdowns/basic-dropdown';
import StrategyLabels from './strategy-labels';
import Modal from '../../base/modals/base-modal';
import Tooltip from '../../misc/tooltip';

import './strategy.scss';

// TODO: A lot of the validation etc is the same as the parent, refactor for DRY
class Strategy extends Component {
  constructor(props) {
    super(props);

    this.strategyContainer = React.createRef();
    this.strategyStatus = React.createRef();

    this.strategyStatusFields = [
      { label: 'Active', value: true, selected: false },
      { label: 'Paused', value: false, selected: false },
    ];

    const strategyStatusFields = this.strategyStatusFields.map(status => {
      status.selected = status.value === this.props.strategy.is_enabled;
      return status;
    });

    this.triggerTypes = ['brand', 'category'];
    this.triggerMap = { brand: 'brands', category: 'categories' };

    const isFlatRateMerchant =
      this.props.pricingModel === 'EPP' && this.props.strategy.bid !== null;
    const flatOrCpc = isFlatRateMerchant || this.props.pricingModel === 'CPC';

    // Matches price formats: $x,xxx.xx, xx.x, etc.
    this.currencyRegex = /^\$?\d*(?:,?\d{3})*(?:\.\d{1,2})?$/g;
    // Matches positive integers and floats up to two decimal places
    this.roiRegex = /^\d{0,6}(?:\.\d{0,2})?$/;
    // Matches strings of format utm_param=param1&utm_otherparam=param2
    this.utmRegex = /^([\w]+=[\w%+-]*)(&([\w]+=[\w%+-]+))*$/g;
    // Matches formats with quotes or braces
    this.quotesRegex = /(['"“”])((\\['"“”]|.)*?)['"“”]/gm;
    // Matches no quotes categories, optionally comma delimited
    this.noQuotesRegex = /^(([\w\s&>-])+(')?([\w\s&>-])+)(,([\w\s&>-])+(')?([\w\s&>-])+)*$/g;

    // Ensure CPC and ROI values remain under $1000
    this.maxStrategyValue = 1000;

    // Ensure cogs value is between 0 and 100
    this.minCogs = 0;
    this.maxCogs = 100;

    // Ensure Budget values remain under $9,999,999.99
    this.maxBudget = 9999999.99;

    this.priorityFields = [
      { label: '1 Very High', value: 'very_high', selected: false },
      { label: '2 High', value: 'high', selected: false },
      { label: '3 Normal', value: 'normal', selected: false },
      { label: '4 Low', value: 'low', selected: false },
      { label: '5 Very Low', value: 'very_low', selected: false },
    ];

    const priorityFields = this.priorityFields.map(priority => {
      priority.selected = priority.value === this.props.strategy.priority;
      return priority;
    });

    this.paymentModelFields = [
      { label: 'ROI', value: 'publisher_roi_target', model: 'EPP', selected: false },
      { label: 'CPC', value: 'publisher_epc', model: 'EPC', selected: false },
      { label: 'COGS', value: 'cogs', model: null, selected: true },
    ];

    this.paymentModelFieldsMap = this.paymentModelFields.reduce((obj, item) => {
      return { ...obj, [item.label]: item.value };
    }, {});

    this.defaultState = {
      strategy: this.props.strategy,

      // Strategy name
      strategyNameInput: this.props.strategy.name,

      // Strategy budget spent
      budgetSpent: (this.props.strategy.budget_spent * 0.01 || 0).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),

      // Strategy status
      strategyStatus: strategyStatusFields.find(status => status.selected),

      // Triggers
      brand: {
        value: '',
        selected: this.props.strategy.strategy_id
          ? !!this.props.strategy.triggers.brands.length
          : true,
      },
      category: {
        value: '',
        selected: this.props.strategy.strategy_id
          ? !!this.props.strategy.triggers.categories.length
          : true,
      },

      // Strategy Info
      strategyType: this.props.strategy.strategy_id
        ? flatOrCpc
          ? 'cpc'
          : 'roi'
        : this.props.defaultStrategyType, // [roi, cpc]
      roiGoal: this.props.strategy.roi_goal || '', // ROI input value
      manualClickValue: this.props.strategy.bid || '', // CPC input value
      strategyValueInput: this.props.strategy.bid
        ? formatValue(this.props.strategy.bid, 'currency')
        : this.props.strategy.roi_goal || '',

      // Budget
      budgetAmount: this.props.strategy.budget_amount
        ? formatValue(this.props.strategy.budget_amount, 'currency')
        : '',

      // UTM
      utmInput: this.props.strategy.utm_query_string || '',

      // Priority
      priority: priorityFields.find(priority => priority.selected),

      // Modal
      showModal: false,

      // Payment model
      paymentModelVisible:
        this.props.strategy.cogs ||
        this.props.strategy.publisher_roi_target ||
        this.props.strategy.publisher_epc,
      paymentModelType: this.getPaymentModelType(),
      paymentModelInput: this.getPaymentModelInput(),

      // Input field errors
      errors: {
        utm: false,
        triggers: false,
        cpc: false,
        roi: false,
        budgetAmount: false,
      },
    };

    this.state = {
      changesMade: !this.props.strategy.strategy_id,
      initialPubs: this.props.initialPubs,
      ...cloneDeep(this.defaultState),
    };
    this.handleInputResize = this.handleInputResize.bind(this);
  }

  componentDidMount() {
    this.handleInputResize();
    this.setInitialErrorState();
    this.strategyContainer.current.addEventListener('input', this.handleInputResize);
    this.updateInitialPubs();
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.strategy, this.props.strategy)) {
      this.defaultState.strategy = this.props.strategy;
      this.setState({ strategy: this.defaultState.strategy });
    }
  }

  componentWillUnmount() {
    this.strategyContainer.current.removeEventListener('input', this.handleInputResize);
  }

  // Event listeners
  handleInputResize() {
    const ref = this.strategyContainer.current;
    const strategyNameInput = ref.getElementsByClassName('strategy-name-input')[0];
    const brandTriggerInput = ref.getElementsByClassName('brand-trigger-input')[0];
    const categoryTriggerInput = ref.getElementsByClassName('category-trigger-input')[0];
    const brandInput = ref.getElementsByClassName('brand-input')[0];
    const categoryInput = ref.getElementsByClassName('category-input')[0];
    const utmInput = ref.getElementsByClassName('utm-input')[0];

    this.setTextAreaHeight(strategyNameInput);
    this.setTextAreaHeight(utmInput);

    if (brandInput) this.setTextAreaHeight(brandInput);
    if (brandTriggerInput) this.setTextAreaHeight(brandTriggerInput);

    if (categoryInput) this.setTextAreaHeight(categoryInput);
    if (categoryTriggerInput) this.setTextAreaHeight(categoryTriggerInput);
  }

  setInitialErrorState() {
    // New strategies should set error state for mandatory fields by default
    this.props.setError({
      strategyName: !this.state.strategy.strategy_id,
      trigger: !this.state.strategy.strategy_id,
      startDate: !this.state.strategy.strategy_id,
    });
  }

  setTextAreaHeight(el) {
    el.style.height = '40px';
    if (el.clientHeight !== el.scrollHeight) el.style.height = `${el.scrollHeight}px`;
    return el;
  }

  updateInitialPubs() {
    const { triggers } = this.state.strategy;
    const pubs = cloneDeep(this.props.initialPubs).map(pub => {
      if (triggers.publishers && triggers.publishers.includes(pub.pub_id)) {
        pub.selected = true;
      }
      return pub;
    });

    this.setState({
      initialPubs: pubs,
    });
  }

  updateStrategy(updates, changes = true) {
    if (changes) {
      this.setChangesMade(true);
    }

    this.setState({
      strategy: {
        ...this.state.strategy,
        ...updates,
      },
    });
  }

  // Strategy Name and Status
  updateStrategyName(e) {
    // TODO: temporary length validation, remove once server-side validation is re-enabled
    if (e.target.value.length > 255) return;

    this.setChangesMade(true);

    this.setState(
      {
        strategy: {
          ...this.state.strategy,
          name: e.target.value,
        },
        strategyNameInput: e.target.value,
      },
      () => {
        this.props.setError({ strategyName: !this.state.strategyNameInput });
      },
    );
  }

  selectStrategyStatus(index) {
    this.setChangesMade(true);

    this.setState({
      strategy: {
        ...this.state.strategy,
        is_enabled: this.strategyStatusFields[index].value,
      },
      strategyStatus: this.strategyStatusFields[index],
    });
  }

  // Triggers
  selectTrigger(trigger) {
    this.setChangesMade(true);

    this.setState(currentState => {
      const selected = !currentState[trigger].selected;
      return {
        ...(!selected && {
          strategy: {
            ...currentState.strategy,
            triggers: {
              ...currentState.strategy.triggers,
              [this.triggerMap[trigger]]: [], // Remove trigger entries if deselected
            },
          },
        }),
        [trigger]: {
          ...currentState[trigger],
          selected,
        },
      };
    });
  }

  updateTriggerInput(trigger, e) {
    this.setChangesMade(true);

    this.setState({
      [trigger]: {
        ...this.state[trigger],
        value: e.target.value,
      },
    });
  }

  validateTriggers() {
    return (
      this.state.strategy.triggers.brands.length || this.state.strategy.triggers.categories.length
    );
  }

  processBrandInput(input) {
    // Simple comma delimited format for multi-chips only
    if (input.trim().length === 0) {
      return [];
    }

    const items = input.split(',').filter(item => item);
    return items.map(item => item.trim());
  }

  processCategoryInput(input) {
    /*
     * Matches all text between quotes, e.g.
     * `["Google Product Category 1": "Apparel", "Google Product Category 2": "Shoes"]`
     * or '["apparel & accessories", "clothing"]'.
     * Convert to format like 'apparel & accessories > clothing' for consistent payload.
     *
     * Handles comma separated simple inputs for entering multiple chips at once, e.g.
     * shoes,purses,wallets.
     */
    const matched = input.match(this.quotesRegex);
    const noQuotesMatched = input.match(this.noQuotesRegex);

    if (matched) {
      // One chip at a time only
      let i = 0;
      while (i < matched.length) {
        if (matched[i].includes('Google Product Category')) {
          matched.splice(i, 1);
        } else {
          i++;
        }
      }
      return matched
        ? [
            matched
              .map(item => {
                return item.replace(/['"“”:]+/g, '').trim();
              })
              .join(' > '),
          ]
        : [];
    }
    if (noQuotesMatched) {
      // 1 or more chips, regex matches whole input or nothing
      const matches = noQuotesMatched[0].split(',');
      return matches
        ? matches.map(item => {
            if (item.includes('>')) {
              const hierarchyArray = item.split('>');
              return hierarchyArray
                .map(level => {
                  return level.trim();
                })
                .join(' > ');
            }
            return item.trim();
          })
        : [];
    }
    return [];
  }

  newUpdateChips(triggerName, chipArr, deleteValue, replace) {
    let chips = cloneDeep(this.state.strategy.triggers[triggerName]);
    if (deleteValue) {
      if (Array.isArray(deleteValue)) {
        chips = [];
      } else {
        chips = chips.filter(val => val !== deleteValue);
      }
    } else if (replace) {
      chips = chipArr;
    } else {
      chips = chips.filter(val => !chipArr.includes(val)).concat(chipArr);
    }

    const chipsChanged = !isEqual(chips, this.state.strategy.triggers[triggerName]);
    const changesMade = chipsChanged ? true : this.state.changesMade;
    this.setState(currentState => {
      return {
        changesMade,
        strategy: {
          ...currentState.strategy,
          triggers: {
            ...currentState.strategy.triggers,
            [triggerName]: chips,
          },
        },
      };
    });
  }

  updateChips(trigger, chipValue) {
    this.setChangesMade(true);
    let chips = cloneDeep(this.state.strategy.triggers[this.triggerMap[trigger]]);

    // chipValue only passed for deletion
    if (chipValue) {
      chips = chips.filter(value => value !== chipValue);
    } else {
      const chipInput =
        trigger === 'category'
          ? this.processCategoryInput(this.state[trigger].value)
          : this.processBrandInput(this.state[trigger].value);

      chipInput.forEach(chip => chips.push(chip));
    }

    this.setState(
      currentState => {
        const updates = {
          strategy: {
            ...currentState.strategy,
            triggers: {
              ...currentState.strategy.triggers,
              [this.triggerMap[trigger]]: chips,
            },
          },
          [trigger]: {
            ...currentState[trigger],
            value: '',
          },
        };

        const { triggers } = currentState.strategy;
        if (triggers.brands.length || triggers.categories.length) {
          updates.errors = {
            ...currentState.errors,
            triggers: false,
          };
        }

        return updates;
      },
      () => {
        const { triggers } = this.state.strategy;
        this.handleInputResize();
      },
    );
  }

  handleChipKeyInputs(event, trigger) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.updateChips(trigger);
    } else if (
      !this.state[trigger].value &&
      (event.key === 'Backspace' || event.key === 'Delete')
    ) {
      event.preventDefault();
      const currentChips = this.state.strategy.triggers[this.triggerMap[trigger]];
      this.updateChips(trigger, currentChips[currentChips.length - 1]);
    }
  }

  // Strategy Info
  selectStrategyType(type) {
    this.setChangesMade(true);

    // Clear other strategy
    const key = type === 'roi' ? 'bid' : 'roi_goal';

    this.setState(
      {
        strategy: {
          ...this.state.strategy,
          [key]: null,
        },
        strategyType: type,
        strategyValueInput:
          type === 'roi'
            ? this.state.roiGoal
            : formatValue(this.state.manualClickValue, 'currency'),
      },
      () => {
        // Reset errors on other strategy
        this.props.setError({ [type === 'roi' ? 'cpc' : 'roi']: false });
      },
    );
  }

  updateStrategyInput(e) {
    this.setChangesMade(true);

    this.setState({
      strategyValueInput: e.target.value,
    });
  }

  validateStrategyInput(value) {
    const input = value.toString();
    const floatInput = parseFloat(input.replace(/\$|,/g, ''));
    const type = this.state.strategyType;

    const isValidInput =
      type === 'cpc'
        ? input && floatInput <= this.maxStrategyValue && input.match(this.currencyRegex)
        : input && floatInput <= this.maxStrategyValue && input.match(this.roiRegex);

    this.setState(
      currentState => {
        const key = type === 'roi' ? 'roi_goal' : 'bid';
        return {
          ...(isValidInput && {
            strategy: {
              ...currentState.strategy,
              [key]: input.replace(/\$|,/g, ''),
            },
            strategyValueInput:
              type === 'cpc' && value !== ''
                ? formatValue(input.replace(/\$|,/g, ''), 'currency')
                : value,
          }),
          errors: {
            ...currentState.errors,
            [currentState.strategyType]: !isValidInput,
          },
        };
      },
      () => {
        this.props.setError({ [type]: !isValidInput });
      },
    );

    return isValidInput;
  }

  // Budget
  setBudget(e) {
    this.setChangesMade(true);

    this.setState({
      budgetAmount: e.target.value,
    });
  }

  validateBudgetInput(input) {
    const inputValue = input || '';
    const budget = parseFloat(inputValue.replace(/\$|,/g, ''));
    const isValidBudget =
      !inputValue.length ||
      (budget > 0 && budget <= this.maxBudget && inputValue.match(this.currencyRegex));

    this.setState(
      currentState => {
        // Get rid of pretty currency formatting for backend payload
        const budgetAmount = inputValue.length
          ? parseFloat(inputValue.replace(/\$|,/g, '')).toFixed(2)
          : null;
        return {
          ...(isValidBudget && {
            strategy: {
              ...currentState.strategy,
              budget_amount: budgetAmount,
            },
            budgetAmount: inputValue.length ? formatValue(budgetAmount, 'currency') : inputValue,
          }),
          errors: {
            ...currentState.errors,
            budgetAmount: !isValidBudget,
          },
        };
      },
      () => {
        this.props.setError({ budgetAmount: !isValidBudget });
      },
    );

    return isValidBudget;
  }

  // UTM
  updateUTM(e) {
    this.setChangesMade(true);

    this.setState({
      utmInput: e.target.value,
    });
  }

  validateUtm(utmInput) {
    // UTM is an optional field, no input should pass validation
    const isValidUtm =
      !utmInput.length || (utmInput.length < 512 ? utmInput.match(this.utmRegex) : false);

    this.setState(
      currentState => {
        return {
          ...(isValidUtm && {
            strategy: {
              ...currentState.strategy,
              utm_query_string: utmInput || null,
            },
          }),
          errors: {
            ...currentState.errors,
            utm: !isValidUtm,
          },
        };
      },
      () => {
        this.props.setError({ utm: !isValidUtm });
      },
    );

    return isValidUtm; // boolean for testing
  }

  // Priority
  selectPriority(index) {
    this.setChangesMade(true);

    this.setState({
      strategy: {
        ...this.state.strategy,
        priority: this.priorityFields[index].value,
      },
      priority: this.priorityFields[index],
    });
  }

  // Toggle Action Buttons
  setChangesMade(bool) {
    if (bool === this.state.changesMade) return;
    this.setState({ changesMade: bool });
    this.props.setChangesMade(bool, this.state.strategy.strategy_id);
  }

  /* TODO: Currently only triggers are validated on save,
   * add further validation on V2 of feature.
   */
  validateStrategy() {
    this.setState({
      errors: {
        ...this.state.errors,
        triggers: !this.validateTriggers(),
      },
    });
  }

  // Revert all user changes to strategy
  cancelStrategy() {
    this.setChangesMade(false);
    this.setState(() => {
      return {
        ...(!isEqual(this.defaultState.strategy, this.state.strategy) && {
          ...this.defaultState,
        }),
        showModal: false,
      };
    });
    this.props.discardStrategy();
  }

  // Save user changes to strategy
  async saveStrategy() {
    // Temporarily disabled by request of Biz
    // this.validateStrategy();

    if (!session.isAdmin) {
      // eslint-disable no-alert
      alert('Ability to save is currently disabled.');
      return;
    }

    await this.setPaymentModelFields();
    this.setChangesMade(!this.props.hasNoError('strategyErrors'));
    this.props.saveStrategy(this.state.strategy, this.props.strategyIdx);
  }

  updateDescription(e) {
    // TODO: temporary length validation, remove once server-side validation is re-enabled
    if (e.target.value.length > 512) return;

    this.setChangesMade(true);

    this.setState({
      strategy: {
        ...this.state.strategy,
        description: e.target.value,
      },
    });
  }

  togglePaymentModel() {
    this.setChangesMade(true);
    this.setState(
      {
        paymentModelVisible: !this.state.paymentModelVisible,
        paymentModelInput: '',
      },
      () => {
        const strategyCopy = cloneDeep(this.state.strategy);
        if (!this.state.paymentModelVisible) {
          Object.keys(this.paymentModelFieldsMap).forEach(key => {
            // Clear all payment model fields
            strategyCopy[this.paymentModelFieldsMap[key]] = null;
          });
          strategyCopy.payment_model = null;
        } else {
          const selected = this.paymentModelFieldsMap[this.getPaymentModelType()];
          strategyCopy.payment_model = this.paymentModelFields.find(
            model => model.value === selected,
          ).model;
        }
        this.setState({ strategy: strategyCopy });
      },
    );
  }

  getPaymentModelType() {
    if (this.props.strategy.payment_model) {
      this.paymentModelFields.map(field => {
        field.selected = field.model === this.props.strategy.payment_model;
        return field;
      });
    }

    return this.paymentModelFields.find(model => model.selected).label;
  }

  // Default payment model input
  getPaymentModelInput() {
    if (!this.props.strategy.payment_model) {
      return this.props.strategy.cogs ? `${this.props.strategy.cogs}%` : '';
    }

    const modelMap = {
      EPP: this.props.strategy.publisher_roi_target,
      EPC: this.props.strategy.publisher_epc
        ? formatValue(this.props.strategy.publisher_epc.replace(/\$|,/g, ''))
        : '',
    };

    return modelMap[this.props.strategy.payment_model];
  }

  setPaymentModel(index) {
    this.setChangesMade(true);
    const paymentModelOptions =
      this.props.pricingModel === 'EPP'
        ? this.paymentModelFields
        : this.paymentModelFields.slice(1);

    const field = this.paymentModelFieldsMap[paymentModelOptions[index].label];
    this.setState({
      strategy: {
        ...this.state.strategy,
        payment_model: paymentModelOptions[index].model,
      },
      paymentModelType: paymentModelOptions[index].label,
      paymentModelInput: this.state.strategy[field] || '',
    });
  }

  // Triggered upon saving strategy
  setPaymentModelFields() {
    const strategyCopy = cloneDeep(this.state.strategy);
    Object.keys(this.paymentModelFieldsMap).forEach(key => {
      if (key !== this.state.paymentModelType) {
        // Clear all other fields, only one payment model input should have a value
        strategyCopy[this.paymentModelFieldsMap[key]] = null;
      }
    });

    this.setState({ strategy: strategyCopy });
  }

  updatePaymentModelInput(e) {
    this.setChangesMade(true);

    this.setState({
      paymentModelInput: e.target.value,
    });
  }

  validatePaymentModelInput(value) {
    const input = value.toString();
    const floatInput = parseFloat(input.replace(/\$|,/g, ''));
    const type = this.state.paymentModelType;

    const validationMap = {
      ROI: input && floatInput <= this.maxStrategyValue && input.match(this.roiRegex),
      CPC: input && floatInput <= this.maxStrategyValue && input.match(this.currencyRegex),
      COGS: input && input >= this.minCogs && input <= this.maxCogs,
    };

    const formatMap = {
      ROI: value,
      CPC: value !== '' ? formatValue(input.replace(/\$|,/g, ''), 'currency') : value,
      COGS: `${value}%`,
    };

    const isValidInput = validationMap[type];

    this.setState(
      currentState => {
        const key = this.paymentModelFieldsMap[type];

        return {
          ...(isValidInput && {
            strategy: {
              ...currentState.strategy,
              [key]: input.replace(/\$|,/g, ''),
            },
            paymentModelInput: formatMap[type],
          }),
          errors: {
            ...currentState.errors,
            [currentState.paymentModelType]: !isValidInput,
          },
        };
      },
      () => {
        this.props.setError({ [type]: !isValidInput });
      },
    );

    return isValidInput;
  }

  render() {
    const datetimeCreated = dateService.formatDate(
      this.props.strategy.datetime_created,
      'MM/DD/YY, hh:mm A',
    );
    const datetimeUpdated = dateService.formatDate(
      this.props.strategy.datetime_updated,
      'MM/DD/YY, hh:mm A',
    );

    const budgetAmount = parseFloat(this.state.budgetAmount.replace(/\$|,/g, ''));
    const spentAmount = parseFloat(this.state.budgetSpent.replace(/\$|,/g, ''));
    const outOfBudget = budgetAmount > 0 && spentAmount >= budgetAmount;
    const budgetError = outOfBudget || this.state.errors.budgetAmount;
    const budgetMsg = outOfBudget
      ? 'Out of budget'
      : this.state.errors.budgetAmount
      ? 'Invalid budget'
      : 'Optional';

    const budgetSpent =
      this.state.strategy.strategy_id && this.props.showBudgetSpent ? (
        <div
          ref={node => new Tooltip(node, { text: 'Budget Spent', size: 2 })}
          className={`budget-spent${outOfBudget ? ' error' : ''}`}
        >
          {this.state.budgetSpent}
        </div>
      ) : null;

    const triggerBtns = this.triggerTypes.map(trigger => {
      return (
        <button
          key={`${trigger}-btn`}
          className={`${trigger}-btn ${this.state[trigger].selected ? 'selected' : ''}`}
          onClick={() => this.selectTrigger(trigger)}
        >
          {trigger}
        </button>
      );
    });

    const triggerInputs = this.triggerTypes.map(trigger => {
      const chips = this.state.strategy.triggers[this.triggerMap[trigger]].map(triggerValue => {
        const chipValue =
          trigger === 'category'
            ? triggerValue.split(' > ').pop() // Only show last item in list
            : triggerValue;
        return (
          <div key={`${triggerValue}-chip`} className={`chip-container ${trigger}`}>
            <div className="chip-label">{chipValue}</div>
            <div className="delete-chip" onClick={() => this.updateChips(trigger, triggerValue)} />
          </div>
        );
      });

      const errorMsg = this.state.errors.triggers ? (
        <div className="error-msg">Brand or Category is required</div>
      ) : null;

      return this.state[trigger].selected ? (
        <div key={`${trigger}-input-${this.state.strategy.id}`}>
          <div className={`${trigger}-label`}>{trigger}</div>
          <div className={`${trigger}-trigger-input${this.state.errors.triggers ? ' error' : ''}`}>
            {chips}
            <textarea
              className={`${trigger}-input`}
              value={this.state[trigger].value}
              placeholder={`Enter ${trigger} string`}
              spellCheck={false}
              onChange={e => this.updateTriggerInput(trigger, e)}
              onBlur={() => this.updateChips(trigger)}
              onKeyDown={e => this.handleChipKeyInputs(e, trigger)}
            />
          </div>
          {errorMsg}
        </div>
      ) : null;
    });

    const updateActions =
      this.state.changesMade && !this.props.saveInProgress ? (
        <>
          <button className="cancel-btn" onClick={() => this.setState({ showModal: true })}>
            Cancel
          </button>
          <button className="save-btn" onClick={() => this.saveStrategy()}>
            Save
          </button>
        </>
      ) : null;

    const confirmationModal = this.state.showModal ? (
      <Modal
        options={{
          identifier: 'strategy-confirmation',
          displayMsg: 'Discard changes?',
          displayTxt: 'If you leave now your edits will not be saved.',
          save: {
            txt: 'Discard',
            callback: this.cancelStrategy.bind(this),
          },
          cancel: {
            txt: 'Cancel',
            callback: () => this.setState({ showModal: false }),
          },
        }}
      />
    ) : null;

    const newTypeaheads = session.isAdminView ? (
      <>
        {/* TODO: Unhide once finished, admins keep trying to use WIP features */}
        {/* <StrategyTypeahead */}
        {/*	type="category" */}
        {/*	triggerProp={this.state.strategy.triggers.categories} */}
        {/*	updateChips={this.newUpdateChips.bind(this)} */}
        {/* /> */}
        {/* <StrategyTypeahead */}
        {/*	type="brand" */}
        {/*	triggerProp={this.state.strategy.triggers.brands} */}
        {/*	updateChips={this.newUpdateChips.bind(this)} */}
        {/* /> */}
        <StrategyTypeahead
          type="publisher"
          triggerProp={this.state.strategy.triggers.publishers}
          updateChips={this.newUpdateChips.bind(this)}
          initialPubs={this.state.initialPubs}
          cogs={this.state.strategy.cogs}
          updateStrategy={this.updateStrategy.bind(this)}
        />
        <ProductTypeahead
          type="product"
          triggerProp={this.state.strategy.triggers.skus}
          updateChips={this.newUpdateChips.bind(this)}
        />
      </>
    ) : null;

    const strategyLabels = (
      <StrategyLabels
        labelsProp={this.state.strategy.labels}
        updateLabels={labels =>
          this.setState({
            changesMade: true,
            strategy: {
              ...this.state.strategy,
              labels,
            },
          })
        }
      />
    );

    const feeType = session.isAdmin ? (
      <StrategyFeeType
        feeTypeProp={this.state.strategy.fee_type}
        strategyId={this.state.strategy.strategy_id}
        updateFeeType={feeType =>
          this.setState(
            {
              strategy: {
                ...this.state.strategy,
                fee_type: feeType,
              },
            },
            () => this.setChangesMade(true),
          )
        }
      />
    ) : null;

    const strategyVisibility = session.isAdmin ? (
      <StrategyVisibility
        visibilityProp={this.state.strategy.visibility}
        strategyId={this.state.strategy.strategy_id}
        updateVisibility={visibility =>
          this.setState(
            {
              strategy: {
                ...this.state.strategy,
                visibility,
              },
            },
            () => this.setChangesMade(true),
          )
        }
      />
    ) : null;

    const errorMessage = this.state.errors[this.state.paymentModelType] ? (
      <div className="error-label">{`Invalid ${this.state.paymentModelType.toUpperCase()}`}</div>
    ) : null;

    const paymentModelOptions =
      this.props.pricingModel === 'EPP'
        ? this.paymentModelFields
        : this.paymentModelFields.slice(1);

    const paymentModelError = this.state.errors[this.state.paymentModelType];
    const expectedMargin = this.props.strategy.netMargin ? (
      <div className="expected-margin">
        {`Expected Margin for this campaign is ${this.props.strategy.netMargin}%`}
      </div>
    ) : null;
    const paymentModel =
      this.state.paymentModelVisible && session.isAdminView ? (
        <div className="payment-model-container">
          <div className="payment-model-label">Payment Model</div>
          <BasicDropdown
            items={paymentModelOptions}
            selectItem={idx => this.setPaymentModel(idx)}
            dropdownLabel={this.state.paymentModelType}
          />
          <div className="is">is</div>
          <input
            className={`payment-model-input${paymentModelError ? ' error' : ''}`}
            placeholder={`Set ${this.state.paymentModelType}`}
            value={this.state.paymentModelInput}
            onChange={event => this.updatePaymentModelInput(event)}
            onBlur={() => this.validatePaymentModelInput(this.state.paymentModelInput)}
          />
          {expectedMargin}
          {errorMessage}
          <div className="admin-only-label">Admin Only</div>
        </div>
      ) : null;

    return (
      <div
        ref={this.strategyContainer}
        tabIndex="0"
        className={`strategy-container${this.state.changesMade ? ' changes' : ''}`}
        onFocus={() => this.strategyContainer.current.classList.toggle('selected')}
        onBlur={() => this.strategyContainer.current.classList.toggle('selected')}
      >
        <div className="strategy-header">
          <textarea
            className="strategy-name-input"
            value={this.state.strategyNameInput}
            placeholder="Add strategy name"
            spellCheck={false}
            onChange={e => this.updateStrategyName(e)}
          />
          {budgetSpent}
          <div ref={this.strategyStatus} className="strategy-status">
            <div
              className={getClass('status-dot', {
                active: this.state.strategyStatus.label === 'Active',
                paused: this.state.strategyStatus.label === 'Paused',
              })}
            />
            <BasicDropdown
              dropdownLabel={this.state.strategyStatus.label}
              handleDropdown={() => this.strategyStatus.current.classList.toggle('open')}
              items={this.strategyStatusFields}
              selectItem={idx => this.selectStrategyStatus(idx)}
            />
          </div>
        </div>

        <div className="trigger-btn-container">{triggerBtns}</div>
        <div className="description-container">
          <div className="label">Description</div>
          <textarea
            className="input"
            value={this.state.strategy.description}
            spellCheck={false}
            onChange={e => this.updateDescription(e)}
          />
        </div>
        {feeType}
        {strategyLabels}
        <div className="trigger-input-container">{triggerInputs}</div>

        {newTypeaheads}

        <div className="select-strategy-container">
          <StrategySelection
            strategyType={this.state.strategyType}
            selectStrategyType={this.selectStrategyType.bind(this)}
            strategyValueInput={this.state.strategyValueInput}
            updateStrategyValue={this.updateStrategyInput.bind(this)}
            validateStrategyInput={this.validateStrategyInput.bind(this)}
            pricingModel={this.props.pricingModel}
            togglePaymentModel={this.togglePaymentModel.bind(this)}
            paymentModelVisible={this.state.paymentModelVisible}
            paymentToggleVisible={true}
            hasError={this.state.errors[this.state.strategyType]}
          />
        </div>

        {paymentModel}

        <StrategyDatePicker
          datetimeStart={this.state.strategy.datetime_start}
          datetimeEnd={this.state.strategy.datetime_end}
          updateStrategy={this.updateStrategy.bind(this)}
          setError={this.props.setError}
        />

        <div className="budget-container">
          <div className="budget-label">Budget</div>
          <input
            className={`budget-input${budgetError ? ' error' : ''}`}
            value={this.state.budgetAmount}
            placeholder="Budget"
            maxLength="20"
            onChange={e => this.setBudget(e)}
            onBlur={() => this.validateBudgetInput(this.state.budgetAmount)}
          />
          <div className={`optional${budgetError ? ' error' : ''}`}>{budgetMsg}</div>
        </div>

        <div className="utm-container">
          <div className="utm-label">UTM</div>
          <textarea
            className={`utm-input${this.state.errors.utm ? ' error' : ''}`}
            value={this.state.utmInput}
            placeholder="UTM Parameters"
            spellCheck={false}
            onChange={e => this.updateUTM(e)}
            onBlur={() => this.validateUtm(this.state.utmInput)}
          />
          <div className={`optional${this.state.errors.utm ? ' error' : ''}`}>
            {this.state.errors.utm ? 'UTM not recognized' : 'Optional'}
          </div>
        </div>

        <div className="priority-container">
          <div className="priority-label">Priority</div>
          <BasicDropdown
            items={this.priorityFields}
            selectItem={idx => this.selectPriority(idx)}
            dropdownLabel={this.state.priority.label}
          />
        </div>
        {strategyVisibility}
        <div className={`line${this.state.changesMade ? ' changes' : ''}`} />
        <div className="actions">
          <div className="datetime-groups">
            <div>{datetimeCreated}</div>
            <div>{datetimeUpdated}</div>
          </div>
          {updateActions}
        </div>
        {confirmationModal}
      </div>
    );
  }
}

export default Strategy;
